import { useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import './ContentUploadSection.css'

interface ContentFormData {
  title: string
  description: string
  category: string
  difficulty?: string
  duration?: string
  mediaUrl: string
  codeUrl: string
  location?: string
  date?: string
}

const ContentUploadSection = () => {
  const { user } = useAuth()
  const [activeForm, setActiveForm] = useState<'tutorial' | 'creation' | 'schedule'>('tutorial')
  const [useAI, setUseAI] = useState(false)
  const [aiPrompt, setAiPrompt] = useState('')
  const [aiGenerating, setAiGenerating] = useState(false)
  const [formData, setFormData] = useState<ContentFormData>({
    title: '',
    description: '',
    category: '',
    difficulty: '',
    duration: '',
    mediaUrl: '',
    codeUrl: '',
    location: '',
    date: '',
  })

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) return
    setAiGenerating(true)

    // Simulate AI generation (in production, call OpenAI API)
    await new Promise(resolve => setTimeout(resolve, 2000))

    // Mock AI response
    const mockResponse = {
      title: aiPrompt.includes('sonic pi') ? 'getting started with sonic pi' : 'advanced strudel patterns',
      description: `A comprehensive guide covering ${aiPrompt.toLowerCase()}. This tutorial includes step-by-step instructions, code examples, and best practices for creating engaging live-coded compositions.`,
      category: aiPrompt.includes('sonic') ? 'Sonic Pi' : aiPrompt.includes('strudel') ? 'Strudel' : 'General',
      difficulty: 'intermediate',
      duration: '60 min',
    }

    setFormData(prev => ({
      ...prev,
      ...mockResponse,
    }))
    setAiGenerating(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!formData.title.trim() || !formData.description.trim() || !formData.category) {
      alert('Please fill in all required fields')
      return
    }

    if (activeForm === 'tutorial') {
      if (!formData.difficulty || !formData.duration) {
        alert('Please fill in difficulty and duration for tutorials')
        return
      }
      let tutorials: any[] = []
      try {
        tutorials = JSON.parse(localStorage.getItem('tutorials') || '[]')
      } catch (e) {
        console.error('Error reading tutorials:', e)
      }
      const newTutorial = {
        id: `tut_${Date.now()}`,
        title: formData.title.trim(),
        description: formData.description.trim(),
        category: formData.category,
        difficulty: formData.difficulty,
        duration: formData.duration,
        resources: [], // Resources can be added via the modal after creation
        createdAt: new Date().toISOString(),
      }
      tutorials.push(newTutorial)
      localStorage.setItem('tutorials', JSON.stringify(tutorials))
      alert('Tutorial created successfully! You can add resources by clicking the resources button on the tutorial card.')
    } else if (activeForm === 'creation') {
      const { error: insertError } = await supabase
        .from('creations')
        .insert({
          author_id: user?.id,
          title: formData.title.trim(),
          description: formData.description.trim(),
          category: formData.category,
          media_url: formData.mediaUrl || null,
        })
      if (insertError) {
        alert('Failed to create: ' + insertError.message)
        return
      }
      alert('Creation uploaded successfully!')
    } else if (activeForm === 'schedule') {
      if (!formData.title.trim() || !formData.location || !formData.description.trim()) {
        alert('Please fill in all required fields for schedule items')
        return
      }
      let scheduleItems: any[] = []
      try {
        scheduleItems = JSON.parse(localStorage.getItem('schedule_items') || '[]')
      } catch (e) {
        console.error('Error reading schedule_items:', e)
      }
      const newScheduleItem = {
        id: `schedule_${Date.now()}`,
        title: formData.title.trim(),
        location: formData.location,
        description: formData.description.trim(),
        nextSessionUrl: formData.mediaUrl || '',
        nextSessionDate: formData.date || '',
      }
      scheduleItems.push(newScheduleItem)
      localStorage.setItem('schedule_items', JSON.stringify(scheduleItems))
      alert('Schedule item added successfully!')
    }

    // Reset form
    setFormData({
      title: '',
      description: '',
      category: '',
      difficulty: '',
      duration: '',
      mediaUrl: '',
      codeUrl: '',
      location: '',
      date: '',
    })
    setAiPrompt('')
  }

  return (
    <div className="content-upload-section">
      <h2 className="admin-section-title">content creation</h2>
      <p className="admin-section-description">
        Create beautiful content for the signal. Use AI assistance or fill in manually.
      </p>

      <div className="content-form-tabs">
        <button
          className={`content-form-tab ${activeForm === 'tutorial' ? 'active' : ''}`}
          onClick={() => setActiveForm('tutorial')}
        >
          <i className="fas fa-book"></i>
          tutorial
        </button>
        <button
          className={`content-form-tab ${activeForm === 'creation' ? 'active' : ''}`}
          onClick={() => setActiveForm('creation')}
        >
          <i className="fas fa-palette"></i>
          creation
        </button>
        <button
          className={`content-form-tab ${activeForm === 'schedule' ? 'active' : ''}`}
          onClick={() => setActiveForm('schedule')}
        >
          <i className="fas fa-calendar"></i>
          schedule
        </button>
      </div>

      <div className="ai-assistant-toggle">
        <label className="ai-toggle-label">
          <input
            type="checkbox"
            checked={useAI}
            onChange={(e) => setUseAI(e.target.checked)}
          />
          <span>use ai assistant</span>
        </label>
      </div>

      {useAI && (
        <div className="ai-prompt-section">
          <textarea
            className="ai-prompt-input"
            placeholder="Describe what you want to create... (e.g., 'A beginner tutorial on Sonic Pi for creating ambient textures')"
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            rows={3}
          />
          <button
            className="admin-button admin-button-primary"
            onClick={handleAIGenerate}
            disabled={!aiPrompt.trim() || aiGenerating}
          >
            <i className="fas fa-magic"></i>
            {aiGenerating ? 'generating...' : 'generate with ai'}
          </button>
        </div>
      )}

      <form className="content-upload-form" onSubmit={handleSubmit}>
        <input
          type="text"
          className="admin-input"
          placeholder="title"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          required
        />

        <textarea
          className="admin-input admin-textarea"
          placeholder="description"
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={6}
          required
        />

        {activeForm === 'tutorial' && (
          <>
            <div className="admin-form-row">
              <select
                className="admin-input"
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                required
              >
                <option value="">select category</option>
                <option value="Sonic Pi">Sonic Pi</option>
                <option value="Strudel">Strudel</option>
                <option value="Playtronica">Playtronica</option>
                <option value="Touch Designer">Touch Designer</option>
              </select>
              <select
                className="admin-input"
                value={formData.difficulty}
                onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                required
              >
                <option value="">select difficulty</option>
                <option value="beginner">beginner</option>
                <option value="intermediate">intermediate</option>
                <option value="advanced">advanced</option>
              </select>
              <input
                type="text"
                className="admin-input"
                placeholder="duration (e.g., 45 min)"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                required
              />
            </div>
          </>
        )}

        {activeForm === 'creation' && (
          <select
            className="admin-input"
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            required
          >
            <option value="">select category</option>
            <option value="audio">audio</option>
            <option value="visual">visual</option>
            <option value="interactive">interactive</option>
            <option value="installation">installation</option>
            <option value="code">code</option>
          </select>
        )}

        {activeForm === 'schedule' && (
          <>
            <input
              type="text"
              className="admin-input"
              placeholder="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              required
            />
            <input
              type="datetime-local"
              className="admin-input"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              required
            />
          </>
        )}

        <input
          type="url"
          className="admin-input"
          placeholder="media url (video, audio, image)"
          value={formData.mediaUrl}
          onChange={(e) => setFormData({ ...formData, mediaUrl: e.target.value })}
        />

        {(activeForm === 'tutorial' || activeForm === 'creation') && (
          <input
            type="url"
            className="admin-input"
            placeholder="code repository url"
            value={formData.codeUrl}
            onChange={(e) => setFormData({ ...formData, codeUrl: e.target.value })}
          />
        )}

        <button type="submit" className="admin-button admin-button-primary">
          <i className="fas fa-upload"></i>
          publish {activeForm}
        </button>
      </form>
    </div>
  )
}

export default ContentUploadSection

